import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Snowflake, Microwave, Refrigerator, Box, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GRADE_DEFECT_CEILINGS, SCALE_EFFECTIVE_DATE_LABEL } from "@shared/grading";

interface CategoryLite {
  id: string;
  slug: string;
  packagingImage: string | null;
  packagingDescription: string | null;
}

const PACKAGING_FALLBACK: Record<string, { image: string | null; description: string }> = {
  refrigerators: {
    image: null,
    description:
      "Each refrigerator is carefully wrapped in protective plastic, secured with heavy-duty strapping, and reinforced with corner protectors to ensure safe delivery.",
  },
  "air-conditioners": {
    image: null,
    description:
      "Air conditioners ship in one of three box configurations (EOB / EGB / EAB). See the Box Types section above for full details.",
  },
  microwaves: {
    image: null,
    description:
      "Every microwave ships in an EOB-equivalent original retail box with a supplier label identifying the unit and model number.",
  },
};

function CategoryPackagingSection({ slug, categories }: { slug: string; categories: CategoryLite[] | undefined }) {
  const cat = categories?.find((c) => c.slug === slug);
  const fallback = PACKAGING_FALLBACK[slug] ?? { image: null, description: "" };
  const image = cat?.packagingImage ?? fallback.image;
  const description = cat?.packagingDescription ?? fallback.description;

  return (
    <section className="max-w-6xl mx-auto mb-16" data-testid={`section-packaging-${slug}`}>
      <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 min-h-[280px] md:min-h-[360px] flex items-center justify-center">
            {image ? (
              <>
                <img
                  src={image}
                  alt="Packaging example"
                  className="w-full h-full object-contain max-h-[420px]"
                  data-testid={`img-packaging-${slug}`}
                />
                <span
                  className="pointer-events-none absolute inset-0 flex items-center justify-center text-white font-extrabold text-5xl md:text-7xl tracking-[0.3em] opacity-90"
                  style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}
                  data-testid={`overlay-example-${slug}`}
                >
                  EXAMPLE
                </span>
              </>
            ) : (
              <div className="text-slate-600 text-sm italic px-6 text-center">
                Packaging example coming soon.
              </div>
            )}
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-testid={`text-packaging-title-${slug}`}>
              Packaging
            </h3>
            <p
              className="text-base md:text-lg text-slate-300 leading-relaxed whitespace-pre-line"
              data-testid={`text-packaging-description-${slug}`}
            >
              {description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
import aGradeImg from "@assets/a_1763307959198.png";
import aMinusGradeImg from "@assets/Black_Vinyl_Wrap_1782412951154.jpg";
import bPlusGradeImg from "@assets/b_1763307959198.png";
import bMinusGradeImg from "@assets/c_1763307959198.png";
import finalPackagingImg from "@assets/final_1763309380397.png";
import refrigeratorImg from "@assets/stock_images/modern_refrigerator__fd7b0729.jpg";
import smallAppliancesImg from "@assets/stock_images/small_kitchen_applia_04dc74e4.jpg";
import transparencyVideo from "@assets/Screen Recording 2025-11-28 at 09.26.50_1764362555850.webm";
import eab1 from "@assets/sohnen_eab_1_1777481841378.png";
import eab2 from "@assets/sohnen_eab_2_1777481841378.jpg";
import eab3 from "@assets/sohnen_eab_3_1777481841378.jpg";
import eab4 from "@assets/sohnen_eab_4_1777481841378.jpg";
import eabAcImg from "@assets/IMG_1071_1777859685181.jpeg";

// Hash → tab value mapping. The same value is used both as the tab id
// and as the URL anchor we link to from cart/checkout/PDP/catalog FR pills.
const HASH_TO_TAB: Record<string, string> = {
  refrigerators: "refrigerators",
  "air-conditioners": "air-conditioners",
  microwaves: "microwaves",
};

function readTabFromHash(): string {
  if (typeof window === "undefined") return "refrigerators";
  const raw = window.location.hash.replace(/^#/, "").trim().toLowerCase();
  return HASH_TO_TAB[raw] ?? "refrigerators";
}

export default function GradingPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeTab, setActiveTab] = useState<string>(() => readTabFromHash());
  const { data: categories } = useQuery<CategoryLite[]>({ queryKey: ["/api/categories"] });

  // Keep the active tab in sync with the URL hash so links like
  // /grading#air-conditioners deep-link straight to the AC section.
  useEffect(() => {
    const sync = () => setActiveTab(readTabFromHash());
    window.addEventListener("hashchange", sync);
    // Run once on mount in case the hash arrived after first paint
    sync();
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  useEffect(() => {
    document.title = "Grading & Packaging | OpenBox by ShopJimmy";

    const description =
      `Updated cosmetic grading scale (effective ${SCALE_EFFECTIVE_DATE_LABEL}): ` +
      `A grade has no front-facing defects, A- grade has its front professionally restored and ` +
      `covered with an automotive-grade vinyl wrap, B+ allows up to ${GRADE_DEFECT_CEILINGS["B+"]} ` +
      `small front blemishes, B− up to ${GRADE_DEFECT_CEILINGS["B-"]}. Every unit is fully ` +
      `functional with the original factory warranty.`;

    let metaTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = metaTag ? metaTag.content : null;
    const createdMetaTag = !metaTag;
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.name = "description";
      document.head.appendChild(metaTag);
    }
    metaTag.content = description;

    return () => {
      document.title = "OpenBox by ShopJimmy";
      if (createdMetaTag && metaTag?.parentNode) {
        metaTag.parentNode.removeChild(metaTag);
      } else if (metaTag && previousDescription !== null) {
        metaTag.content = previousDescription;
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          // Mirror the active tab back into the URL hash so deep-links remain
          // shareable when users click between tabs in-page.
          if (typeof window !== "undefined" && v) {
            const newHash = `#${v}`;
            if (window.location.hash !== newHash) {
              history.replaceState(null, "", newHash);
            }
          }
        }} className="w-full">
          <TabsList className="bg-[#2d2d2d] border border-gray-700 mb-8 mx-auto flex w-full max-w-2xl justify-center" data-testid="tabs-grading-categories">
            <TabsTrigger value="refrigerators" className="flex-1 data-[state=active]:bg-[#7DB428] data-[state=active]:text-white" data-testid="tab-refrigerators">
              <Refrigerator className="h-4 w-4 mr-2" />
              Refrigerators
            </TabsTrigger>
            <TabsTrigger value="air-conditioners" className="flex-1 data-[state=active]:bg-[#7DB428] data-[state=active]:text-white" data-testid="tab-air-conditioners">
              <Snowflake className="h-4 w-4 mr-2" />
              Air Conditioners
            </TabsTrigger>
            <TabsTrigger value="microwaves" className="flex-1 data-[state=active]:bg-[#7DB428] data-[state=active]:text-white" data-testid="tab-microwaves">
              <Microwave className="h-4 w-4 mr-2" />
              Microwaves
            </TabsTrigger>
          </TabsList>

          <TabsContent value="refrigerators" className="mt-0">
        {/* Hero Section - Final Packaging + Definition */}
        <section id="refrigerators" className="max-w-6xl mx-auto mb-16 relative scroll-mt-24">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-8 items-stretch">
            {/* Left - Final Packaging */}
            <div className="group relative bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl overflow-hidden hover:border-[rgba(125,180,40,0.4)] transition-all duration-300">
              <div className="relative h-[400px] md:h-[500px] overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
                <img 
                  src={finalPackagingImg} 
                  alt="Final Packaging" 
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-40" />
              </div>
              <div className="p-6">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Protective Packaging</h3>
                <p className="text-sm md:text-base text-slate-400 leading-relaxed">
                  Each refrigerator is carefully wrapped in protective plastic, secured with heavy-duty strapping, and reinforced with corner protectors to ensure safe delivery.
                </p>
              </div>
            </div>

            {/* Right - Dictionary Definition */}
            <div 
              className="bg-white rounded-sm shadow-lg p-5 md:p-7 flex flex-col"
              style={{ 
                fontFamily: "'Georgia', 'Times New Roman', serif",
                lineHeight: 1.6,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              data-testid="dictionary-definition"
            >
              {/* DEFINITION label */}
              <div className="text-[11px] font-bold text-black tracking-widest mb-3">
                DEFINITION
              </div>
              
              {/* Word and pronunciation */}
              <div className="mb-1">
                <span className="text-2xl md:text-[28px] font-bold text-gray-900">
                  Open Box — <em>ShopJimmy Style</em>
                </span>
              </div>
              <div className="text-sm md:text-base text-gray-500 mb-2">
                | ˈōpən ˈbäks ˌshäpˈjimē stīl |
              </div>
              
              {/* Phonetic pronunciation */}
              <div className="text-gray-400 mb-3 text-sm">
                <span className="italic">OH</span>-puhn <span className="italic">BAHKS</span> — <span className="italic">shop</span>-JIM-ee <span className="italic">STYL</span>
              </div>
              
              {/* Part of speech */}
              <div className="italic text-gray-600 mb-3 text-base">
                noun
              </div>
              
              {/* Definition 1 */}
              <div className="mb-3 text-gray-800 text-base leading-relaxed">
                <span className="font-bold">1</span> An appliance originally sold and delivered to a customer by a major U.S. retailer but rejected at delivery for non-functional reasons—such as fit, size, color, or minor installation blemishes—and therefore no longer classified as "new," even if never plugged in.
              </div>
              
              {/* Definition 2 */}
              <div className="mb-3 text-gray-800 text-base leading-relaxed">
                <span className="font-bold">2</span> A fully functional, factory-backed unit purchased by ShopJimmy at a discount and inspected through a strict, transparent process, including:
                <ul className="mt-2 ml-5 list-disc space-y-1 text-sm">
                  <li>Complete removal of original packaging</li>
                  <li>Full power-on testing and verification</li>
                  <li>Confirmation of all accessories</li>
                  <li>Detailed cleaning and preparation</li>
                  <li>Cosmetic grading of all front-facing surfaces (A, A−, B+, or B−)</li>
                  <li>Item-specific video documentation with visual markers identifying any flaws</li>
                </ul>
              </div>
              
              {/* Definition 3 */}
              <div className="mb-3 text-gray-800 text-base leading-relaxed">
                <span className="font-bold">3</span> A premium, discounted appliance priced well below MSRP, with complete visibility into condition, functionality, and cosmetic details before purchase.
              </div>
              
              {/* Origin */}
              <div className="border-t border-gray-200 mt-auto pt-3 text-[13px] text-gray-500">
                <span className="italic">Origin:</span> early 21st century retail terminology, redefined by ShopJimmy to emphasize transparency and quality assurance.
              </div>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div 
            className="absolute -right-4 md:right-[-80px] top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 cursor-pointer group"
            onClick={() => {
              document.getElementById('grading-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="scroll-indicator"
          >
            <span className="text-slate-400 text-xs font-medium whitespace-nowrap rotate-90 md:rotate-0 origin-center group-hover:text-[#7DB428] transition-colors">
              Scroll to CR Images
            </span>
            <ChevronDown className="h-6 w-6 text-slate-400 animate-bounce group-hover:text-[#7DB428] transition-colors" />
          </div>
        </section>

        {/* Grading System Section */}
        <section id="grading-section" className="max-w-6xl mx-auto mb-24">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight">
              Transparency You Can See
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl">
              All refrigerators work like new. The only difference is cosmetic appearance.
            </p>
            <div
              className="mt-6 inline-flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 max-w-3xl"
              data-testid="callout-grading-effective-date"
            >
              <span className="text-amber-300 text-xl leading-none mt-0.5" aria-hidden="true">📣</span>
              <p className="text-sm md:text-base text-amber-100/90 leading-snug">
                <strong className="text-amber-100">Updated grading scale, effective {SCALE_EFFECTIVE_DATE_LABEL}:</strong>{' '}
                B+ refrigerators now allow up to {GRADE_DEFECT_CEILINGS['B+']} small front-facing
                blemishes (was 3) and B− allows up to {GRADE_DEFECT_CEILINGS['B-']} (was 5). The
                cosmetic-only definition is unchanged — every unit ships fully functional.
              </p>
            </div>
          </div>

          {/* Grade cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { 
                grade: 'A GRADE', 
                desc: 'Looks New', 
                details: '✅ Perfect front — no visible blemishes',
                badgeColors: 'bg-green-100 text-green-800 border-green-300',
                accentColor: 'from-green-400 to-green-500',
                hoverBorder: 'rgba(34, 197, 94, 0.4)',
                hoverShadow: 'rgba(34, 197, 94, 0.2)',
                image: aGradeImg
              },
              { 
                grade: 'A- GRADE', 
                desc: 'Restored & Vinyl-Wrapped', 
                details: '✨ Front previously dented — professionally filled and covered with an automotive-grade vinyl wrap. Looks stunning, not the original factory finish.',
                badgeColors: 'bg-blue-100 text-blue-800 border-blue-400',
                accentColor: 'from-blue-400 to-blue-500',
                hoverBorder: 'rgba(59, 130, 246, 0.4)',
                hoverShadow: 'rgba(59, 130, 246, 0.2)',
                image: aMinusGradeImg
              },
              { 
                grade: 'B+ GRADE', 
                desc: 'Minor Imperfections', 
                details: `⚪ Up to ${GRADE_DEFECT_CEILINGS['B+']} small front blemishes`,
                badgeColors: 'bg-orange-100 text-orange-800 border-orange-300',
                accentColor: 'from-orange-400 to-orange-500',
                hoverBorder: 'rgba(249, 115, 22, 0.4)',
                hoverShadow: 'rgba(249, 115, 22, 0.2)',
                image: bPlusGradeImg
              },
              { 
                grade: 'B– GRADE', 
                desc: 'Noticeable Wear', 
                details: `⚫ Up to ${GRADE_DEFECT_CEILINGS['B-']} front blemishes`,
                badgeColors: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                accentColor: 'from-yellow-400 to-yellow-500',
                hoverBorder: 'rgba(234, 179, 8, 0.4)',
                hoverShadow: 'rgba(234, 179, 8, 0.2)',
                image: bMinusGradeImg
              }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="group relative bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2"
                style={{
                  borderColor: 'rgb(75, 85, 99)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = item.hoverBorder;
                  e.currentTarget.style.boxShadow = `0 20px 60px ${item.hoverShadow}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgb(75, 85, 99)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`grade-card-${idx}`}
              >
                {/* Top accent bar */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${item.accentColor} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`} />
                
                {/* Image */}
                <div className="relative h-[450px] overflow-hidden rounded-t-xl mb-6 -mx-6 -mt-6 bg-gradient-to-br from-slate-900 to-slate-800">
                  <img 
                    src={item.image} 
                    alt={`${item.grade} refrigerator example`}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 mt-[20px] mb-[20px] pt-[20px] pb-[20px]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-40 pointer-events-none" />
                </div>

                <div className="px-6 pb-6">
                  <div className={`inline-block ${item.badgeColors} border-2 px-6 py-2.5 rounded-full font-bold text-lg mb-5 tracking-wide`}>
                    {item.grade}
                  </div>
                  <div className="text-lg text-slate-300 mb-5 font-medium">
                    {item.desc}
                  </div>
                  <div className="text-base text-slate-400 leading-relaxed">
                    {item.details}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Video showcase */}
          <div className="mt-16 text-center">
            <p className="text-xl md:text-2xl font-bold text-white mb-6">
              You've never seen open box appliances presented like this — <span className="text-[#7DB428]">anywhere in the world.</span>
            </p>
            <div className="relative rounded-2xl overflow-hidden border-2 border-gray-700 hover:border-[rgba(125,180,40,0.5)] transition-all duration-300 shadow-[0_0_60px_rgba(0,0,0,0.5)]">
              <video
                ref={videoRef}
                src={transparencyVideo}
                muted
                loop
                playsInline
                className="w-full h-auto"
                data-testid="video-transparency"
              />
            </div>
          </div>
        </section>

        {/* Product showcase with images */}
        <section className="max-w-6xl mx-auto mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Factory Warranty */}
            <div className="group relative bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl overflow-hidden hover:border-[rgba(125,180,40,0.4)] transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={refrigeratorImg} 
                  alt="Refrigerator" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-90" />
              </div>
              <div className="p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Factory Warranty Included
                </h3>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed">
                  Every refrigerator comes with a <strong className="text-[#7DB428]">1-Year Factory Warranty</strong> — the same coverage you'd get if you bought new. 
                  Enjoy peace of mind knowing your open box appliance is fully supported by the manufacturer.
                </p>
              </div>
            </div>

            {/* FOB Burnsville */}
            <div className="group relative bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl overflow-hidden hover:border-[rgba(125,180,40,0.4)] transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={smallAppliancesImg} 
                  alt="Appliances" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-90" />
              </div>
              <div className="p-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  FOB Burnsville, MN
                </h3>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed">All items are available for pickup or shipping from our Burnsville, Minnesota warehouse. All sales are final.</p>
              </div>
            </div>
          </div>
        </section>
          </TabsContent>

          <TabsContent value="air-conditioners" className="mt-0">
            <section id="air-conditioners" className="max-w-6xl mx-auto mb-12 scroll-mt-24" data-testid="section-ac-grading">
              <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight" data-testid="text-ac-grading-title">
                  Air Conditioner Grading
                </h1>
                <p className="text-lg md:text-xl text-slate-400 max-w-3xl">
                  Every air conditioner we sell is <span className="text-[#7DB428] font-semibold">B grade</span>: fully functional, like-new, with only minor cosmetic blemishes. Every unit ships with the original factory warranty.
                </p>
              </div>

              {/* Factory-Refurbished vs open-box B callout — linked from the
                  green "B — Factory Refurbished" pill on AC pallet listings,
                  PDPs, the cart drawer, and checkout. */}
              <div
                className="bg-emerald-950/30 border-2 border-emerald-500/60 rounded-2xl p-6 md:p-8 mb-12"
                data-testid="callout-factory-refurbished-vs-openbox"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full border-2 border-emerald-400 bg-emerald-100 text-emerald-800">
                    <Sparkles className="h-4 w-4" />
                    B — Factory Refurbished
                  </span>
                  <span className="text-slate-500 text-sm">vs.</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full border-2 border-slate-400 bg-slate-100 text-slate-800">
                    B (Open Box)
                  </span>
                </div>
                <p className="text-base text-slate-300 leading-relaxed mb-3">
                  All air conditioners on our site today carry the
                  green <span className="font-semibold text-emerald-400">B — Factory Refurbished</span> badge.
                  Refurbished units are returned to the manufacturer, fully restored
                  to like-new specification, retested, and reboxed before being
                  sold by the pallet.
                </p>
                <p className="text-base text-slate-400 leading-relaxed">
                  Standard open-box B grade (the gray pill) is used elsewhere on
                  the site for refrigerators and other categories where the unit
                  was returned to a retailer but never sent back to the factory.
                  Both grades are fully functional and warrantied — the green
                  pill simply tells you the unit went through the manufacturer's
                  refurbishment program.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Sparkles className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Fully Functional</h3>
                  <p className="text-sm text-slate-400">Power-tested and verified. Cools to spec. No mechanical defects.</p>
                </div>
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Snowflake className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">1-Year Warranty</h3>
                  <p className="text-sm text-slate-400">Same factory warranty you'd get buying brand new from a major retailer.</p>
                </div>
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Box className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Sold by the Pallet</h3>
                  <p className="text-sm text-slate-400">Curated multi-SKU pallets at MN01. Mix of brands, BTU sizes, and box types.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6 md:p-8 mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" data-testid="text-box-types-title">Box Types</h2>
                <p className="text-base text-slate-400 mb-6">
                  Each unit on a pallet ships in one of three box configurations. The box type is shown on every product card and noted in the pallet composition.
                </p>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start" data-testid="row-box-eob">
                    <div className="inline-flex items-center justify-center bg-blue-100 text-blue-800 border-2 border-blue-300 px-6 py-3 rounded-full font-bold text-lg">
                      EOB
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-1">Original Box</h4>
                      <p className="text-sm text-slate-400">
                        Ships in the manufacturer's original retail box. Box may have minor handling marks from inspection. All original packaging materials and accessories included.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">Photo example coming soon.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start" data-testid="row-box-egb">
                    <div className="inline-flex items-center justify-center bg-amber-100 text-amber-800 border-2 border-amber-300 px-6 py-3 rounded-full font-bold text-lg">
                      EGB
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-1">Generic Brown Box</h4>
                      <p className="text-sm text-slate-400">
                        Original retail box was damaged or unavailable. Repacked in a plain brown shipping carton with all original accessories and inserts. Unit itself is unaffected.
                      </p>
                      <p className="text-xs text-slate-500 mt-2 italic">Photo example coming soon.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start" data-testid="row-box-eab">
                    <div className="inline-flex items-center justify-center bg-purple-100 text-purple-800 border-2 border-purple-300 px-6 py-3 rounded-full font-bold text-lg">
                      EAB
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-1">Brown Box with Artwork Label</h4>
                      <p className="text-sm text-slate-400">
                        Repacked in a brown shipping carton with the manufacturer's artwork and branding printed directly on the box, plus a supplier label identifying the unit and model number.
                      </p>
                      <div className="mt-4 w-full max-w-xs rounded-lg overflow-hidden border border-gray-700 bg-slate-900" data-testid="img-eab-ac-midea">
                        <img src={eabAcImg} alt="EAB — Midea window AC in brown box with artwork" className="w-full h-auto object-contain" />
                        <p className="text-xs text-slate-500 px-3 py-2 italic">Example: Midea 5,000 BTU window AC — EAB carton</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">EAB carton examples</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[eab1, eab2, eab3, eab4].map((src, i) => (
                      <div key={i} className="aspect-square overflow-hidden rounded-lg border border-gray-700 bg-slate-900" data-testid={`img-eab-${i + 1}`}>
                        <img src={src} alt={`EAB carton example ${i + 1}`} className="w-full h-full object-contain" />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3 italic">EGB photo example coming soon.</p>
                </div>
              </div>
            </section>
            <CategoryPackagingSection slug="air-conditioners" categories={categories} />
          </TabsContent>

          <TabsContent value="microwaves" className="mt-0">
            <section id="microwaves" className="max-w-6xl mx-auto mb-12 scroll-mt-24" data-testid="section-mw-grading">
              <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 tracking-tight" data-testid="text-microwave-grading-title">
                  Microwave Grading
                </h1>
                <p className="text-lg md:text-xl text-slate-400 max-w-3xl">
                  Every microwave we sell is <span className="text-[#7DB428] font-semibold">B grade</span> — factory-refurbished, fully functional, like-new, with the original factory warranty. Sold by the pallet at MN01.
                </p>
              </div>

              <div
                className="bg-emerald-950/30 border-2 border-emerald-500/60 rounded-2xl p-6 md:p-8 mb-12"
                data-testid="callout-mw-factory-refurbished"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full border-2 border-emerald-400 bg-emerald-100 text-emerald-800">
                    <Sparkles className="h-4 w-4" />
                    B — Factory Refurbished
                  </span>
                </div>
                <p className="text-base text-slate-300 leading-relaxed">
                  Microwaves carry the green <span className="font-semibold text-emerald-400">B — Factory Refurbished</span> badge. Units returned to the manufacturer, restored to like-new specification, retested, and reboxed before being sold by the pallet.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Sparkles className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Fully Functional</h3>
                  <p className="text-sm text-slate-400">Power-tested and verified. Cooks to spec. No mechanical defects.</p>
                </div>
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Microwave className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">1-Year Warranty</h3>
                  <p className="text-sm text-slate-400">Same factory warranty you'd get buying brand new from a major retailer.</p>
                </div>
                <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6">
                  <Box className="h-8 w-8 text-[#7DB428] mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Sold by the Pallet</h3>
                  <p className="text-sm text-slate-400">Single-SKU and curated mixed pallets at MN01. GE countertop models in three sizes.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#2d2d2d] to-[#1a1a1a] border border-gray-700 rounded-2xl p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" data-testid="text-mw-box-types-title">Packaging</h2>
                <p className="text-base text-slate-400 mb-6">
                  Microwaves ship in the manufacturer's original retail box with a supplier label identifying the unit and model number — equivalent to the EOB box type used for air conditioners.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start" data-testid="row-mw-box-eob">
                  <div className="inline-flex items-center justify-center bg-blue-100 text-blue-800 border-2 border-blue-300 px-6 py-3 rounded-full font-bold text-lg">
                    EOB
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Original Retail Box</h4>
                    <p className="text-sm text-slate-400">
                      Each microwave ships in its original factory box with all original packaging materials and accessories included. Box may have minor handling marks from inspection.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <CategoryPackagingSection slug="microwaves" categories={categories} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
